import { useState, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getPendingOrders, getAllOrders, reviewOrder, getCurrentUser } from '../utils/authManager';

export default function AdminPage() {
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState('pending'); // pending, approved, rejected, all
  const [adminNote, setAdminNote] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const currentUser = getCurrentUser();

  const loadOrders = useCallback(() => {
    let allOrders;
    if (filter === 'pending') {
      allOrders = getPendingOrders();
    } else if (filter === 'all') {
      allOrders = getAllOrders();
    } else {
      allOrders = getAllOrders().filter(o => o.status === filter);
    }
    // 按时间倒序排列
    allOrders.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    setOrders(allOrders);
  }, [filter]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  const handleReview = (order) => {
    setSelectedOrder(order);
    setAdminNote('');
    setShowReviewModal(true);
  };

  const handleApprove = () => {
    if (!selectedOrder) return;
    const result = reviewOrder(selectedOrder.id, 'approve', adminNote);
    if (result.success) {
      alert('✅ 订单已通过，订阅已激活');
      setShowReviewModal(false);
      setSelectedOrder(null);
      loadOrders();
    } else {
      alert(result.message);
    }
  };

  const handleReject = () => {
    if (!selectedOrder) return;
    const result = reviewOrder(selectedOrder.id, 'reject', adminNote);
    if (result.success) {
      alert('❌ 订单已拒绝');
      setShowReviewModal(false);
      setSelectedOrder(null);
      loadOrders();
    } else {
      alert(result.message);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending': return '⏳ 待审核';
      case 'approved': return '✅ 已通过';
      case 'rejected': return '❌ 已拒绝';
      default: return status;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return '#ff9800';
      case 'approved': return '#4caf50';
      case 'rejected': return '#f44336';
      default: return '#999';
    }
  };

  // 简单的管理员检查（实际项目中应该使用更安全的验证）
  const isAdmin = currentUser && (currentUser.email === 'admin@aenedu.site' || currentUser.email === 'vip@aenedu.site');

  if (!isAdmin) {
    return (
      <div className="admin-container">
        <header className="app-header">
          <div className="header-left">
            <div className="logo">
              <span className="logo-icon">✈️</span>
              <div>
                <h1>智伴精读</h1>
                <p className="logo-subtitle">AEN 五维一体 · 外刊精读</p>
              </div>
            </div>
          </div>
          <nav className="header-nav">
            <Link to="/" className="nav-link">首页</Link>
            <Link to="/subscription" className="nav-link">订阅中心</Link>
          </nav>
        </header>
        <div className="admin-content" style={{ textAlign: 'center', padding: '80px 20px' }}>
          <h2>🔒 访问受限</h2>
          <p>您没有权限访问管理后台</p>
          <Link to="/" className="btn btn-primary" style={{ marginTop: '20px' }}>
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-container">
      {/* 顶部导航栏 */}
      <header className="app-header">
        <div className="header-left">
          <div className="logo">
            <span className="logo-icon">✈️</span>
            <div>
              <h1>智伴精读</h1>
              <p className="logo-subtitle">AEN 五维一体 · 外刊精读</p>
            </div>
          </div>
        </div>
        <nav className="header-nav">
          <Link to="/" className="nav-link">首页</Link>
          <Link to="/subscription" className="nav-link">订阅中心</Link>
          <Link to="/admin" className="nav-link active">管理后台</Link>
        </nav>
      </header>

      <div className="admin-content" style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
        <h2 style={{ marginBottom: '24px' }}>📊 订单管理后台</h2>

        {/* 统计卡片 */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
          gap: '16px',
          marginBottom: '24px'
        }}>
          <div style={{ 
            background: 'white', 
            padding: '20px', 
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            <h3 style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#666' }}>待审核订单</h3>
            <p style={{ margin: 0, fontSize: '32px', fontWeight: 'bold', color: '#ff9800' }}>
              {getAllOrders().filter(o => o.status === 'pending').length}
            </p>
          </div>
          <div style={{ 
            background: 'white', 
            padding: '20px', 
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            <h3 style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#666' }}>已通过订单</h3>
            <p style={{ margin: 0, fontSize: '32px', fontWeight: 'bold', color: '#4caf50' }}>
              {getAllOrders().filter(o => o.status === 'approved').length}
            </p>
          </div>
          <div style={{ 
            background: 'white', 
            padding: '20px', 
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            <h3 style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#666' }}>总订单数</h3>
            <p style={{ margin: 0, fontSize: '32px', fontWeight: 'bold', color: '#2196f3' }}>
              {getAllOrders().length}
            </p>
          </div>
        </div>

        {/* 筛选标签 */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
          {[
            { key: 'pending', label: '⏳ 待审核' },
            { key: 'approved', label: '✅ 已通过' },
            { key: 'rejected', label: '❌ 已拒绝' },
            { key: 'all', label: '📋 全部' }
          ].map(item => (
            <button
              key={item.key}
              className={`btn ${filter === item.key ? 'btn-primary' : 'btn-outline'}`}
              style={{ fontSize: '14px' }}
              onClick={() => setFilter(item.key)}
            >
              {item.label}
            </button>
          ))}
        </div>

        {/* 订单列表 */}
        <div style={{ background: 'white', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          {orders.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#999' }}>
              <p>暂无订单</p>
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f5f5f5' }}>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px' }}>订单号</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px' }}>用户</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px' }}>套餐</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px' }}>金额</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px' }}>支付方式</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px' }}>支付备注</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px' }}>状态</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px' }}>时间</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px' }}>操作</th>
                </tr>
              </thead>
              <tbody>
                {orders.map(order => (
                  <tr key={order.id} style={{ borderTop: '1px solid #eee' }}>
                    <td style={{ padding: '12px', fontSize: '13px', fontFamily: 'monospace' }}>
                      {order.id}
                    </td>
                    <td style={{ padding: '12px', fontSize: '13px' }}>
                      <div>{order.userNickname}</div>
                      <div style={{ color: '#999', fontSize: '12px' }}>{order.userEmail}</div>
                    </td>
                    <td style={{ padding: '12px', fontSize: '13px' }}>{order.planName}</td>
                    <td style={{ padding: '12px', fontSize: '13px', fontWeight: 'bold' }}>
                      ¥{order.price}
                    </td>
                    <td style={{ padding: '12px', fontSize: '13px' }}>
                      {order.paymentMethod === 'alipay' ? '💙 支付宝' : '💚 微信'}
                    </td>
                    <td style={{ padding: '12px', fontSize: '13px', fontFamily: 'monospace', color: '#e65100' }}>
                      {order.note}
                    </td>
                    <td style={{ padding: '12px', fontSize: '13px' }}>
                      <span style={{ 
                        color: getStatusColor(order.status),
                        fontWeight: 'bold'
                      }}>
                        {getStatusText(order.status)}
                      </span>
                    </td>
                    <td style={{ padding: '12px', fontSize: '12px', color: '#666' }}>
                      {formatDate(order.createdAt)}
                    </td>
                    <td style={{ padding: '12px' }}>
                      {order.status === 'pending' && (
                        <button
                          className="btn btn-primary"
                          style={{ fontSize: '12px', padding: '6px 12px' }}
                          onClick={() => handleReview(order)}
                        >
                          审核
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* 审核弹窗 */}
      {showReviewModal && selectedOrder && (
        <div className="modal-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            width: '90%',
            maxWidth: '500px',
            padding: '24px'
          }}>
            <h3 style={{ margin: '0 0 16px 0' }}>🔍 订单审核</h3>
            
            <div style={{ marginBottom: '16px' }}>
              <p><strong>订单号：</strong>{selectedOrder.id}</p>
              <p><strong>用户：</strong>{selectedOrder.userNickname} ({selectedOrder.userEmail})</p>
              <p><strong>套餐：</strong>{selectedOrder.planName}</p>
              <p><strong>金额：</strong>¥{selectedOrder.price}</p>
              <p><strong>支付方式：</strong>{selectedOrder.paymentMethod === 'alipay' ? '支付宝' : '微信'}</p>
              <p><strong>支付备注：</strong><span style={{ color: '#e65100', fontFamily: 'monospace' }}>{selectedOrder.note}</span></p>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                管理员备注（可选）：
              </label>
              <textarea
                value={adminNote}
                onChange={(e) => setAdminNote(e.target.value)}
                placeholder="输入审核备注..."
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  fontSize: '14px',
                  minHeight: '80px',
                  resize: 'vertical'
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                className="btn btn-outline"
                style={{ flex: 1 }}
                onClick={() => setShowReviewModal(false)}
              >
                取消
              </button>
              <button
                className="btn btn-primary"
                style={{ flex: 1, background: '#f44336' }}
                onClick={handleReject}
              >
                ❌ 拒绝
              </button>
              <button
                className="btn btn-primary"
                style={{ flex: 1, background: '#4caf50' }}
                onClick={handleApprove}
              >
                ✅ 通过
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
